import { Alert, Button, Divider, Grid, Paper, Skeleton, Tab, Typography } from "@mui/material";
import { Localized, useLocalization } from "@fluent/react";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useCallback, useEffect, useState } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import React from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DashboardIcon from '@mui/icons-material/Dashboard';
import { enUS, plPL } from "@mui/x-data-grid/locales";
import { Description } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { loadArchivedProject } from "./archivedProjectsApi";
import { Project } from "../projectSlice";
import { APIError } from "../../../app/appTypes";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { selectSettings } from "../../settings/settingsSlice";
import { RolesLabel } from "../Labels/ProjectRolesLabel";
import { ProjectProcessStatusLabel } from "../projectProcess/ProjectProcessStatusLabel";
import { loadManyBlobs } from "../../documents/attachementsSlice";
import { ProjectHeader } from "../Components/ProjectHeader";
import ProjectOverviewDashboard from "../dashboard/ProjectOverviewDashboard";
import { Attachements } from "../../urs/Attachements";
import { BlobResourceType } from "../../documents/blobResourceTypes";
import { MeetingMinutesList } from "../mettingMinutes/MeetingMinutesList";
import { NotesList } from "../Notes/NotesList";
import { ProjectSectionHeader } from "../Components/ProjectSectionHeader";
import { ProjectKeyValueRow } from "../Components/ProjectKeyValueRow";
import { BusinessCaseListForProject } from "../../businessCase/BusinessCaseListForProject";
import { BottomAppBar } from "../../../app/BottomAppBar";
import { ArchivedProjectAlert } from "../Components/ArchivedProjectAlert";
import { usePackage } from "../../permissions/usePackage";


type FormTabs = "process" | "management" | "notes" | "attachments" | "conversations" | "meetingMinutes" | "businessCases" | "overview"


export const ArchivedProjectForm = () => {
    const { id: paramId } = useParams();
    const projectId = paramId ?? ''
    const dispatch = useAppDispatch()
    const [project, setProject] = useState<Project | undefined>(undefined)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [loadingError, setLoadingError] = useState<APIError | undefined>(undefined)
    const [loadingErrorOccured, setLoadingErrorOccured] = useState<boolean>(false)

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadArchivedProject(projectId))
            .then(unwrapResult)
            .then(project => setProject(project))
            .catch((error) => { console.log(error); setLoadingErrorOccured(true); setLoadingError(error); })
            .finally(() => setIsLoading(false));
    }, [])

    if (loadingErrorOccured) {
        return <>
            <ErrorContainer error={loadingError} />
        </>
    }

    if (isLoading) {
        return <LoadingContainer />
    }

    if (project === undefined) return <Skeleton variant='rectangular' sx={{ minHeight: '400px' }} />

    return <EditForm project={project} setProject={setProject} />
}


interface EditFormProps {
    project: Project
    setProject: React.Dispatch<React.SetStateAction<Project | undefined>>
}
const EditForm = (props: EditFormProps) => {
    const { project, setProject } = props
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const processRows = project?.process || [];
    const allTeamMembers = project?.projectTeamMembers || []
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);

    const [params, setParams] = useSearchParams()
    const tab = params.get('tab') as FormTabs || 'overview'
    const canSeeMeetingMinutes = usePackage("MeettingMinutes")
    const canSeeBusinessCases = usePackage("BusinessCase")
    const setTab = (tab: FormTabs) => {
        params.set('tab', tab)
        setParams(params, { replace: true })
    }


    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const handleTabChange = useCallback((_, newTab) => {
        setTab(newTab)
    }, [])

    const handleBackButtonClick = useCallback(() => {
        navigate(-1)
    }, [navigate])

    const handleGoToClick = (id) => {
        navigate(`/projectprocess/edit/${id}`)
    };

    const teamcolumns: GridColDef[] = [
        {
            field: 'firstName',
            headerName: l10n.getString('user-first-name', null, 'Imię'),
            width: 300
        },
        {
            field: 'surname',
            headerName: l10n.getString('user-last-name', null, 'Nazwisko'),
            flex: 1
        },
        {
            field: 'position',
            headerName: l10n.getString('user-position', null, 'Stanowisko'),
            width: 200
        },
        {
            field: 'companyName',
            headerName: l10n.getString('user-company', null, 'Firma'),
            flex: 1
        },
        {
            field: 'role',
            headerName: l10n.getString('project-role', null, 'Rola w Projekcie'),
            flex: 1,
            renderCell: (params) => (
                <div>
                    <RolesLabel role={params.value ?? ""} />
                </div>
            )
        }
    ];

    const processColumns: GridColDef[] = [
        {
            field: 'name',
            headerName: l10n.getString('project-process', null, 'Proces'),
            flex: 1
        },
        {
            field: 'createDate',
            headerName: l10n.getString('project-create-date', null, 'Data Utworzenia'),
            width: 200,
            valueGetter: (_, row) => {
                return row.createDate ? new Date(row.createDate).toISOString().split('T')[0] : "";
            }
        },
        {
            field: 'status',
            headerName: l10n.getString('project-status', null, 'Status'),
            width: 150,
            renderCell: (params) => (
                <div>
                    <ProjectProcessStatusLabel status={params.row.status ?? ""} />
                </div>
            )
        }
    ];



    useEffect(() => {
        if (project) {
            dispatch(loadManyBlobs([project.guid]))
        }
    }, [])

    if (project === undefined) return <Alert severity="error">Project not found</Alert>

    return <>
        <Paper elevation={0} sx={{ p: 0 }}>
            <ProjectHeader project={project} />
            <ArchivedProjectAlert project={project} />
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TabContext value={tab}>
                        <TabList onChange={handleTabChange}>
                            <Tab
                                key={1}
                                value={"overview"}
                                icon={<DashboardIcon />}
                                iconPosition="start"
                                label={"Dashboard"} />
                            <Tab
                                key={2}
                                value={"management"}
                                label={<Localized id="project-management">Zarządzanie</Localized>} />
                            <Tab
                                key={3}
                                value={"team"}
                                label={<Localized id="project-team">Zespół</Localized>} />
                            <Tab
                                key={4}
                                value={"attachments"}
                                label={<Localized id="project-attachments">Załaczniki</Localized>} />
                            <Tab
                                key={5}
                                value={"process"}
                                label={<Localized id="project-processes-list">Lista procesów</Localized>} />
                            {canSeeMeetingMinutes && <Tab
                                key={6}
                                value={"meetingMinutes"}
                                label={<Localized id="meeting-minutes-minute-list">Lista Notatek</Localized>} />}
                            {canSeeBusinessCases && <Tab
                                key={7}
                                value={"businessCases"}
                                label={"Business cases"} />}
                            <Tab
                                key={8}
                                value={"notes"}
                                label={<Localized id="project-notes">notes</Localized>} />

                        </TabList>
                        <TabPanel value="overview">
                            <ProjectOverviewDashboard
                                projectGuid={project?.guid}
                                projectId={project?.id}
                                editable={false} />
                        </TabPanel>
                        <TabPanel value="process">
                            <DataGrid
                                style={{ minHeight: 100, height: 'calc(100vh - 425px)' }}
                                localeText={getCustomLocaleText()}
                                rows={processRows}
                                columns={processColumns}
                                onRowDoubleClick={({ row }) => handleGoToClick(row.id)} />
                        </TabPanel>
                        <TabPanel value="team">
                            <DataGrid
                                style={{ minHeight: 100, height: 'calc(100vh - 400px)' }}
                                rows={allTeamMembers}
                                columns={teamcolumns}
                            />
                        </TabPanel>
                        <TabPanel value="attachments">
                            <Attachements resourceId={project?.guid} resourceType={BlobResourceType.Project} canDelete={false} canUpload={false} />
                        </TabPanel>
                        <TabPanel value="meetingMinutes">
                            <MeetingMinutesList
                                objectId={project.guid}
                                objectType='PROJECT'
                                canEdit={false}
                            />
                        </TabPanel>
                        <TabPanel value="notes">
                            <NotesList
                                objectId={project.guid}
                                canEdit={false}
                            />
                        </TabPanel>
                        <TabPanel value="management">
                            <Grid container spacing={1}>
                                <Grid item xs={12}>
                                    <Paper variant="outlined" sx={{ p: 1, mb: 2 }}>
                                        <ProjectSectionHeader
                                            icon={<Description fontSize="small" />}
                                            title={<Localized id="project-basic-data">Dane projektu</Localized>}
                                        />
                                        <Divider sx={{ mb: 1.5 }} />
                                        <ProjectKeyValueRow
                                            label={l10n.getString("project-no", null, "_No")}
                                            value={project.no}
                                            showEdit={false}
                                        />

                                        {/* Tytuł projektu */}
                                        <ProjectKeyValueRow
                                            label={l10n.getString("project-title", null, "_Title")}
                                            value={project.name}
                                            showEdit={false}
                                        />

                                        {/* General contractor */}
                                        <ProjectKeyValueRow
                                            label={l10n.getString("project-general-contractor", null, "_GeneralContractor")}
                                            value={project.generalContractor || ""}
                                            showEdit={false}
                                        />

                                        {/* Investor */}
                                        <ProjectKeyValueRow
                                            label={l10n.getString("project-investor", null, "_Investor")}
                                            value={project.investor || ""}
                                            showEdit={false}
                                        />

                                        {/* Opis (HTML) */}
                                        <ProjectKeyValueRow
                                            label={l10n.getString("project-description", null, "_Description")}
                                            value={
                                                <Typography
                                                    variant="body1"
                                                    component="div"  
                                                    fontWeight={600}
                                                    sx={{ ml: 1 }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: project.description ?? "",
                                                    }}
                                                />
                                            }
                                            showEdit={false}
                                        />
                                    </Paper>
                                </Grid>
                            </Grid>
                        </TabPanel>
                        <TabPanel value="businessCases">
                            <BusinessCaseListForProject
                                projectId={project.id}
                                projectStatus={project.status}
                                canEdit={false} />
                        </TabPanel>
                    </TabContext>
                </Grid>
            </Grid>
            <BottomAppBar>
                <Button key="back" onClick={handleBackButtonClick} color="secondary" variant="outlined">
                    <Localized id="back">Wróć</Localized>
                </Button>
            </BottomAppBar>
        </Paper>


    </>
}